import { connect } from 'react-redux';
import { go, goBack, push } from 'redux-first-history';
import AccountInfoPage from './component';
import { saveAccountInfo } from '@rental-manager/actions/OnboardingActions';
import { LANDLORD_STATUS } from '@rental-manager/constants/OnboardingConstants';
import { DEFAULT_ERROR, DEPOSIT_ACCOUNT_UPDATED } from '@rental-manager/constants/NotificationConstants';
import { getIreneDashboard, getLandlordStatus, getPlaidLinkToken } from '@rental-manager/actions/requests';
import { closeNotification, showModalNotification, showPageNotification } from '@common/actions/NotificationActions';
import { trackValidationError } from '@common/actions/TrackingActions';
import { checkRequestActionSuccess } from '@common/paymentsUtils/api';
import paths from '@rental-manager/pathsManifest';
import { isHighRiskOTP } from '@common/paymentsUtils/moveInCostsUtil';
import { logError } from '@zg-rentals/log-error';

const mapStateToProps = (state, { propertyId, landlordStatus, fromDashboard }) => {
  const { acceptTerms, paymentSettings, accountInfo, identityVerificationInfo, moveInCostsSettings } =
    state.payments.landlord.onboarding[propertyId];
  const isHighRiskOTPPresent = isHighRiskOTP(moveInCostsSettings);

  return {
    propertyId,
    onBoarded:
      landlordStatus &&
      landlordStatus !== LANDLORD_STATUS.NOT_STARTED &&
      landlordStatus !== LANDLORD_STATUS.ONBOARDING_STARTED,
    acceptTerms,
    paymentSettings,
    accountInfo,
    idPageAccessed: identityVerificationInfo.idPageAccessed,
    email: state.user.email,
    config: state.payments.landlord.config,
    fromDashboard,
    selectedTaxVerificationType: identityVerificationInfo.selectedTaxVerificationType || '',
    isHighRiskOTPPresent,
    identityVerificationInfo,
    user: state.user,
    currentProperty: state.payments.landlord.property.listingDetails,
  };
};

const mapDispatchToProps = (dispatch, { baseUrl, propertyId, fromDashboard, location, landlordStatus }): any => ({
  onBack: (accountInfo) =>
    Promise.resolve()
      .then(() => dispatch(closeNotification()))
      .then(() => dispatch(saveAccountInfo({ propertyId, accountInfo })))
      .then(() => {
        const fromPhoneVerification = location?.state?.fromWhere === paths.onboarding.phoneVerificationPage;
        return dispatch(fromPhoneVerification ? go(-2) : goBack());
      })
      .catch((err) => {
        logError({ error: err });
        return dispatch(showModalNotification(DEFAULT_ERROR));
      }),
  getPlaidLinkToken: () =>
    Promise.resolve()
      .then(() => dispatch(closeNotification()))
      .then(() => dispatch(getPlaidLinkToken({ propertyId })))
      .catch((err) => {
        logError({ error: err });
        return dispatch(showModalNotification(DEFAULT_ERROR));
      }),
  onUpdatePaymentMethodSuccess: async ({ editMode, isHighRiskOTPPresent }) => {
    try {
      // if user came from dashboard, send them back and reload
      if (fromDashboard) {
        await dispatch(showPageNotification(DEPOSIT_ACCOUNT_UPDATED));
        await dispatch(getIreneDashboard({ propertyId }));
        return dispatch(push(baseUrl));
      }

      // if user requested high risk OTP during MIC flow, handle Experian logic
      const landlordStatusResponse = await dispatch(getLandlordStatus({ propertyId }));
      await checkRequestActionSuccess(landlordStatusResponse);

      const isHighRiskOtpApproved = landlordStatusResponse.payload?.data?.isHighRiskOtpApproved;
      if (isHighRiskOTPPresent && !isHighRiskOtpApproved) {
        return dispatch(push(baseUrl + paths.onboarding.knowledgeBasedAuthenticationPage));
      }

      if (editMode) {
        await dispatch(showModalNotification(DEPOSIT_ACCOUNT_UPDATED));
      }

      // else, redirect to next onboarding page
      return dispatch(push(baseUrl + paths.onboarding.confirmationPage));
    } catch (e) {
      logError({ error: e });
      dispatch(showModalNotification(DEFAULT_ERROR));
      return { error: true };
    }
  },
  onCancel: () =>
    Promise.resolve()
      .then(() => dispatch(closeNotification()))
      .then(() => {
        // if user came from dashboard, send them back
        if (fromDashboard) {
          return dispatch(push(baseUrl));
        }

        // else, send user back to the confirmation page
        return dispatch(push(baseUrl + paths.onboarding.confirmationPage));
      }),
  onClose: () =>
    Promise.resolve()
      .then(() => dispatch(closeNotification()))
      .then(() => {
        if (fromDashboard) {
          return Promise.resolve()
            .then(() => dispatch(push(baseUrl)))
            .then(() => dispatch(getIreneDashboard({ propertyId })));
        }
        return dispatch(push(baseUrl + paths.onboarding.exitPaymentSetup));
      }),
  showModalNotification: (notificationType) => dispatch(showModalNotification(notificationType)),
  closeNotification: () => dispatch(closeNotification()),
  trackValidationError: (data) => dispatch(trackValidationError(data)),
});

const mergeProps = (stateProps, dispatchProps) => ({
  ...stateProps,
  ...dispatchProps,
  onUpdatePaymentMethodSuccess: () => {
    return dispatchProps.onUpdatePaymentMethodSuccess({
      editMode: stateProps.accountInfo.editMode,
      isHighRiskOTPPresent: stateProps.isHighRiskOTPPresent,
    });
  },
  onClose: () => dispatchProps.onClose(stateProps.accountInfo.editMode),
});

export default connect(mapStateToProps, mapDispatchToProps, mergeProps)(AccountInfoPage);
